import React, { useState, useEffect, useMemo, useRef } from 'react'
import SearchBar from './search-bar'
import { makeStyles } from '@material-ui/styles'
import { useIsTablet } from 'lib/hooks'
import StudentItemView from './student-item-view'
import { parseStudents } from './utils'
import EnhancedTable from 'components/tables/enhanced-table'
import StudentsTablePagination from './students-table-pagination'
import PermContactCalendarOutlinedIcon from 'components/material-symbol/icons/PermContactCalendar'
import SectionEmptyState from 'components/section-empty-state'
import ArrowDropDownIcon from 'components/material-symbol/icons/ArrowDropDown'
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Popover,
  Radio,
  RadioGroup,
  TableContainer,
} from '@material-ui/core'
import { onReschedule, onPayment, onCancellation } from 'lib/utils'
import { sortByFullName, sortByUpcoming } from 'lib/student-list/utils'
import SwapVertIcon from 'components/material-symbol/icons/SwapVert'
import { useStudentListShellStyles } from './student-list-shell-styles'
import StudentInsightsSummary from './student-insights-summary'
import { useLocation } from 'react-router-dom'
import { STATUS_FILTER_OPTIONS } from 'components/student-insights/constants'
import {
  matchesStatusFilter,
  sortByRetention,
  statusFilterFromSearch,
} from './insights-utils'

const AGE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Ages' },
  { value: 'adult', label: 'Adult' },
  { value: 'junior', label: 'Junior' },
]

// returns true/false for if student is in selected age group
const isInAgeGroup = ({ isMinor }, desiredGroup) => {
  if (desiredGroup === 'all') {
    return true
  } else if (isMinor && desiredGroup === 'junior') {
    return true
  } else if (!isMinor && desiredGroup === 'adult') {
    return true
  } else {
    return false
  }
}

// true if search term is empty - '' - or contained by name; false otherwise
const nameMatchesSearchToken = ({ firstName, lastName }, token) => {
  const fullName = `${firstName} ${lastName}`.toLowerCase().trim()
  const term = token.toLowerCase().trim()

  return fullName.includes(term)
}

// Needs attention lists the most urgent students first; every other view
// keeps the name sort.
const orderByForStatus = status => (status === 'attention' ? 'status' : 'name')

export const useStyles = makeStyles(theme => ({
  containerStyles: {
    boxSizing: 'border-box',
    padding: theme.spacing(3, 4.5, 2),
    maxWidth: '100%',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '60px',
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(3, 1, 2),
    },
  },
  filterButton: {
    height: 40,
    borderRadius: 8,
    fontWeight: 500,
    flexShrink: 0,
    borderColor: '#C4C7C9',
    color: '#172024',
    '& .MuiButton-endIcon': {
      color: '#6B7C87',
    },
  },
  filterButtonActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: '#E0EAFF',
    color: '#001C57',
    fontWeight: 600,
    '& .MuiButton-endIcon': {
      color: '#001C57',
    },
    '&:hover': {
      backgroundColor: '#D4E2FF',
      borderColor: theme.palette.primary.main,
    },
  },
  filterMenuPaper: {
    marginTop: theme.spacing(0.5),
    minWidth: 200,
  },
  filterMenuContent: {
    padding: theme.spacing(1.5, 2),
    boxSizing: 'border-box',
  },
  filterForm: {
    display: 'flex',
    margin: 0,
    padding: 0,
    border: 0,
    minWidth: 0,
  },
  filterLegend: {
    display: 'block',
    fontSize: '0.6875rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: '#6B7C87',
    padding: 0,
    margin: 0,
    marginBottom: theme.spacing(1),
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  filterFormSpaced: {
    marginTop: theme.spacing(1.5),
  },
  filterRadioGroup: {
    padding: 0,
    margin: 0,
  },
  filterRadioLabel: {
    margin: 0,
    width: '100%',
    minHeight: 36,
    marginLeft: 0,
    marginRight: 0,
    '& .MuiFormControlLabel-label': {
      fontSize: '0.875rem',
      lineHeight: '20px',
      color: '#172024',
    },
    '& .MuiRadio-root': {
      color: '#40484C',
      padding: theme.spacing(1),
    },
    '& .MuiRadio-colorPrimary.Mui-checked': {
      color: '#0053FF',
    },
  },
  statusGroup: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(0.75),
    padding: 0,
    margin: 0,
  },
  statusChip: {
    position: 'relative',
    margin: 0,
    '& .Mui-checked ~ $statusLabel': {
      backgroundColor: '#E0EAFF',
      borderColor: theme.palette.primary.main,
    },
    '& .Mui-focusVisible ~ $statusLabel': {
      outline: '2px solid #0053FF',
      outlineOffset: 2,
    },
  },
  statusRadio: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    padding: 0,
    opacity: 0,
  },
  statusLabel: {
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    width: '100%',
    padding: theme.spacing(1, 1.5),
    border: '1px solid #E5E7EB',
    borderRadius: 20,
    fontSize: '0.8125rem',
    fontWeight: 500,
    lineHeight: '20px',
    color: '#172024',
  },
  statusDot: {
    flexShrink: 0,
    width: 8,
    height: 8,
    marginRight: theme.spacing(1),
    borderRadius: '50%',
  },
}))

export default function StudentListView({
  students,
  sessions,
  refetchStudents,
  refetchSessions,
  showInsights = false,
}) {
  const classes = useStyles()
  const shellClasses = useStudentListShellStyles()
  const isTablet = useIsTablet()
  const { search } = useLocation()
  const [statusFilter, setStatusFilter] = useState(() =>
    showInsights ? statusFilterFromSearch(search) : 'all',
  )
  // EnhancedTable reads initialOrderBy once, so choosing a status (card or
  // Filters menu) remounts the table with a new key and that status's order.
  const [tableSort, setTableSort] = useState({
    key: 0,
    orderBy: orderByForStatus(statusFilter),
  })
  const searchableItems = useMemo(() => parseStudents(students, sessions), [
    students,
    sessions,
  ])
  const [filteredStudents, setFilteredStudents] = useState(searchableItems)
  const [searchTerm, setSearchTerm] = useState('')
  const [ageGroup, setAgeGroup] = useState('all')
  const [filterAnchorEl, setFilterAnchorEl] = useState(null)
  const [page, setPage] = useState(0)
  const rowsPerPage = 25

  // The summary cards honor the search box and Age Group but not the status
  // filter, so each count matches the rows its card shows when clicked. The
  // table rows are these students narrowed by the status filter.
  const summaryStudents = useMemo(
    () =>
      searchableItems.filter(
        item =>
          nameMatchesSearchToken(item, searchTerm) &&
          isInAgeGroup(item, ageGroup),
      ),
    [searchableItems, searchTerm, ageGroup],
  )

  useEffect(() => {
    setFilteredStudents(
      summaryStudents.filter(item => matchesStatusFilter(item, statusFilter)),
    )
    setPage(0)
  }, [summaryStudents, statusFilter])

  const handleStatusSelect = value => {
    setStatusFilter(value)
    setTableSort(({ key }) => ({
      key: key + 1,
      orderBy: orderByForStatus(value),
    }))
  }

  const handleAgeGroupSelect = value => {
    setAgeGroup(value)
  }

  const filterMenuOpen = Boolean(filterAnchorEl)
  // Arrow keys move through a radio group by changing the selection, so keep
  // the menu open for them; keyboard users close it with Escape.
  const arrowKeyChange = useRef(false)
  const trackArrowKey = event => {
    arrowKeyChange.current = event.key.startsWith('Arrow')
  }
  const closeFiltersUnlessArrowKey = () => {
    if (!arrowKeyChange.current) setFilterAnchorEl(null)
    arrowKeyChange.current = false
  }
  const activeFilterLabels = [
    ageGroup !== 'all' &&
      AGE_FILTER_OPTIONS.find(option => option.value === ageGroup)?.label,
    statusFilter !== 'all' &&
      STATUS_FILTER_OPTIONS.find(option => option.value === statusFilter)
        ?.label,
  ].filter(Boolean)
  const hasActiveFilter = activeFilterLabels.length > 0
  const activeFilterLabel = activeFilterLabels.join(', ')
  const filterButtonName = showInsights
    ? 'Student filters'
    : 'Student age group filter'

  const filterButtons = (
    <>
      <Button
        variant="outlined"
        color="default"
        className={`${classes.filterButton} ${
          hasActiveFilter ? classes.filterButtonActive : ''
        }`}
        aria-controls="student-filters-menu"
        aria-haspopup="true"
        aria-expanded={filterMenuOpen}
        aria-label={
          hasActiveFilter
            ? `${filterButtonName}: ${activeFilterLabel} selected`
            : filterButtonName
        }
        onClick={event => setFilterAnchorEl(event.currentTarget)}
        endIcon={<ArrowDropDownIcon />}
      >
        {hasActiveFilter ? activeFilterLabel : 'Filters'}
      </Button>
      <Popover
        id="student-filters-menu"
        open={filterMenuOpen}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          className: classes.filterMenuPaper,
          style: filterAnchorEl
            ? { minWidth: filterAnchorEl.clientWidth }
            : undefined,
        }}
      >
        <Box className={classes.filterMenuContent}>
          <FormControl
            component="fieldset"
            fullWidth
            className={classes.filterForm}
          >
            <FormLabel component="legend" className={classes.filterLegend}>
              Age Group
            </FormLabel>
            <RadioGroup
              aria-label="Age group filter"
              name="age-group-filter"
              className={classes.filterRadioGroup}
              value={ageGroup}
              onKeyDown={showInsights ? trackArrowKey : undefined}
              onChange={event => {
                handleAgeGroupSelect(event.target.value)
                closeFiltersUnlessArrowKey()
              }}
            >
              {AGE_FILTER_OPTIONS.map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  className={classes.filterRadioLabel}
                  control={<Radio color="primary" size="small" />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </FormControl>
          {showInsights ? (
            <FormControl
              component="fieldset"
              fullWidth
              className={`${classes.filterForm} ${classes.filterFormSpaced}`}
            >
              <FormLabel component="legend" className={classes.filterLegend}>
                Engagement Status
              </FormLabel>
              <RadioGroup
                aria-label="Engagement status filter"
                name="booking-status-filter"
                className={classes.statusGroup}
                value={statusFilter}
                onKeyDown={trackArrowKey}
                onChange={event => {
                  handleStatusSelect(event.target.value)
                  closeFiltersUnlessArrowKey()
                }}
              >
                {STATUS_FILTER_OPTIONS.map(option => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    className={classes.statusChip}
                    classes={{ label: classes.statusLabel }}
                    control={
                      <Radio
                        color="primary"
                        size="small"
                        className={classes.statusRadio}
                      />
                    }
                    label={
                      <>
                        <span
                          className={classes.statusDot}
                          style={{
                            backgroundColor: option.dot,
                          }}
                        />
                        {option.label}
                      </>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
          ) : null}
        </Box>
      </Popover>
    </>
  )

  const statusColumn = {
    id: 'status',
    label: 'Engagement Status',
    align: 'left',
    width: isTablet ? '30%' : '26%',
    mobile: true,
    sortable: true,
    sortComparator: sortByRetention,
    inactiveSortIcon: SwapVertIcon,
  }

  const columns = [
    {
      id: 'name',
      label: 'Name',
      align: 'left',
      width: isTablet
        ? showInsights
          ? '50%'
          : '80%'
        : showInsights
          ? '32%'
          : '40%',
      mobile: true,
      sortable: true,
      sortComparator: sortByFullName,
    },
    ...(showInsights ? [statusColumn] : []),
    {
      id: 'upcoming',
      label: 'Next Upcoming Session',
      align: 'left',
      width: showInsights ? '32%' : '40%',
      mobile: false,
      sortable: true,
      sortComparator: sortByUpcoming,
      inactiveSortIcon: SwapVertIcon,
    },
    {
      id: 'actions',
      label: '',
      align: 'right',
      width: isTablet ? '20%' : '10%',
      mobile: true,
      sortable: false,
    },
  ]

  const pageCount =
    Math.ceil((filteredStudents?.length || 0) / rowsPerPage) || 1

  const rows = filteredStudents?.map(student => (
    <StudentItemView
      student={student}
      showInsights={showInsights}
      refetchStudents={refetchStudents}
      key={student.id}
      onReschedule={() => onReschedule(refetchSessions)}
      onPayment={() => onPayment(refetchSessions)}
      onCancellation={() => onCancellation(refetchSessions)}
    />
  ))

  return (
    <TableContainer className={classes.containerStyles}>
      {showInsights ? (
        <StudentInsightsSummary
          students={summaryStudents}
          statusFilter={statusFilter}
          onSelect={handleStatusSelect}
        />
      ) : null}
      <Box className={shellClasses.tableShell}>
        <Box className={shellClasses.toolbar}>
          <Box className={shellClasses.searchGroup}>
            <SearchBar
              className={shellClasses.searchField}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              placeholder="Search Students"
              data-testid="student-list-search"
              style={{ margin: 0 }}
            />
            {filterButtons}
          </Box>
          <Box className={shellClasses.toolbarActions}>
            {filteredStudents.length > 0 && (
              <StudentsTablePagination
                page={page}
                pageCount={pageCount}
                rowsPerPage={rowsPerPage}
                totalCount={filteredStudents.length}
                onPageChange={setPage}
              />
            )}
          </Box>
        </Box>
        <Box className={shellClasses.tableContent}>
          <EnhancedTable
            key={tableSort.key}
            rows={rows}
            columns={columns}
            label="Students"
            numberOfRows={rowsPerPage}
            variant="list"
            rowHeight="56px"
            initialOrderBy={tableSort.orderBy}
            initialOrder="asc"
            page={page}
            onPageChange={setPage}
            showHeaderPagination={false}
            showPaginator={false}
            emptyState={() => (
              <Box className={shellClasses.listEmptyStateContent}>
                <SectionEmptyState
                  Icon={PermContactCalendarOutlinedIcon}
                  title="Students"
                  caption={
                    searchableItems.length === 0
                      ? 'Your students will appear here.'
                      : 'No matching students.'
                  }
                  bordered={false}
                />
              </Box>
            )}
          />
        </Box>
      </Box>
    </TableContainer>
  )
}
